import get from 'lodash.get';
import api from 'shared/api';
import * as actionTypes from './actionTypes';

export const userSections = Object.freeze({
  licensesInfo: 'licensesInfo',
  company: 'company',
  managerPermissions: 'managerPermissions',
  products: 'products',
  licenseTemplates: 'licenseTemplates',
});

const USER_SECTION_ENDPOINTS = Object.freeze({
  [userSections.licensesInfo]: 'licenses_info',
  [userSections.company]: 'company',
  [userSections.managerPermissions]: 'manager_permissions',
  [userSections.products]: 'products',
  [userSections.licenseTemplates]: 'license_templates',
});

const getUserSectionUrl = (userID, section) =>
  `/api/v1/users/${userID}/${USER_SECTION_ENDPOINTS[section]}/`;

// user actions
const initGetUser = (actions) => () => ({
  type: actions.getUserInit,
});

const getUserError = (actions) => () => ({
  type: actions.getUserError,
});

const getUserSuccess = (actions) => (res) => ({
  type: actions.getUserSuccess,
  user: res,
});

const getUserSectionInit = (userID, section) => ({
  type: actionTypes.getUserSectionInit,
  payload: {
    userID,
    section,
  },
});

const getUserSectionSuccess = (userID, section, data) => ({
  type: actionTypes.getUserSectionSuccess,
  payload: {
    userID,
    section,
    data,
  },
});

const getUserSectionError = (userID, section, error, forbidden = false) => ({
  type: actionTypes.getUserSectionError,
  payload: {
    userID,
    section,
    error,
    forbidden,
  },
});

export const getUser = (userID) => (dispatch) => {
  dispatch(initGetUser(actionTypes)());
  return api.get(`/api/v1/users/${userID}`).then(
    (res) => dispatch(getUserSuccess(actionTypes)(get(res, 'data'))),
    () => dispatch(getUserError(actionTypes)()),
  );
};

export const fetchUserSection =
  (userID, section, { force = false } = {}) =>
  (dispatch, getState) => {
    const endpoint = USER_SECTION_ENDPOINTS[section];

    if (!endpoint) {
      return Promise.reject(new Error(`Unknown user section "${section}"`));
    }

    const sectionState = get(
      getState(),
      `user.userSections.${userID}.${section}`,
      null,
    );

    if (!force) {
      if (get(sectionState, 'loading')) {
        return Promise.resolve(get(sectionState, 'data'));
      }

      if (get(sectionState, 'fetched')) {
        return Promise.resolve(get(sectionState, 'data'));
      }
    }

    dispatch(getUserSectionInit(userID, section));

    return api.get(getUserSectionUrl(userID, section)).then(
      (res) => {
        const data = get(res, 'data');
        dispatch(getUserSectionSuccess(userID, section, data));
        return data;
      },
      (error) => {
        const statusCode = get(error, 'response.status');
        const isForbidden = statusCode === 403;

        dispatch(getUserSectionError(userID, section, error, isForbidden));

        if (isForbidden) {
          return null;
        }

        throw error;
      },
    );
  };

export const getUserLicensesInfo = (userID, options) =>
  fetchUserSection(userID, userSections.licensesInfo, options);

export const getUserCompany = (userID, options) =>
  fetchUserSection(userID, userSections.company, options);

export const getUserManagerPermissionsSection = (userID, options) =>
  fetchUserSection(userID, userSections.managerPermissions, options);

export const getUserProducts = (userID, options) =>
  fetchUserSection(userID, userSections.products, options);

export const getUserLicenseTemplates = (userID, options) =>
  fetchUserSection(userID, userSections.licenseTemplates, options);

export const refreshUser = (res) => ({
  type: actionTypes.refreshUser,
  user: res,
});

// login actions
const initLogin = (actions) => () => ({
  type: actions.loginInit,
});

export const loginSuccess = (res) => (dispatch) =>
  dispatch({
    type: actionTypes.loginSuccess,
    user: res,
  });

export const loginError = (res) => (dispatch) =>
  dispatch({
    type: actionTypes.loginError,
    payload: res,
  });

export const login = (loginData) => (dispatch) => {
  dispatch(initLogin(actionTypes)());
  return api.post('/api/v1/auth/login/', loginData);
};

// logout actions
export const logoutSuccess = () => (dispatch) =>
  dispatch({
    type: actionTypes.logoutSuccess,
    user: null,
  });

export const logoutError = () => (dispatch) =>
  dispatch({
    type: actionTypes.logoutError,
  });

export const logout = () => (dispatch) => {
  dispatch({ type: actionTypes.logoutInit });
  return api.get('/api/v1/auth/logout/');
};

// custom domain
export const setCustomDomainData = (data) => (dispatch) =>
  dispatch({
    type: actionTypes.setCustomDomainData,
    data,
  });

// manager permissions
const getManagerPermissionsSuccess = (managerPerm) => ({
  type: actionTypes.getManagerPermissionsSuccess,
  payload: managerPerm,
});

const getManagerPermissionsError = (error) => ({
  type: actionTypes.getManagerPermissionsError,
  payload: error,
});

export const getManagerPermissions = (userID) => (dispatch) => {
  dispatch({ type: actionTypes.getManagerPermissionsInit });
  return api.get(`/api/v1/users/${userID}/manager_permissions`).then(
    (res) => dispatch(getManagerPermissionsSuccess(get(res, 'data'))),
    (err) => dispatch(getManagerPermissionsError(err)),
  );
};

// counter
const getManagerCounterSuccess = (managerCounter) => ({
  type: actionTypes.getManagerCounterSuccess,
  payload: managerCounter,
});

const getManagerCounterError = (error) => ({
  type: actionTypes.getManagerCounterError,
  payload: error,
});

export const getManagerCounter = (userID) => (dispatch) => {
  dispatch({ type: actionTypes.getManagerCounterInit });
  return api.get(`/api/v1/users/${userID}/created_licenses_counts/`).then(
    (res) =>
      dispatch(
        getManagerCounterSuccess(get(res, 'data.created_licenses_counts')),
      ),
    (err) => dispatch(getManagerCounterError(err)),
  );
};
